// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion from "@insite/mobius/Accordion";
import AccordionSection from "@insite/mobius/AccordionSection";
import Typography from "@insite/mobius/Typography";
import parse from "html-react-parser";
import React, { FC } from "react";
import { connect } from "react-redux";

// for widget
const enum fields {
    title = "title",
    accordions = "accordions",
}

// accordions
const enum accordionFields {
    title = "title",
}

interface AccordionsProps {
    fields: {
        [accordionFields.title]: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.accordions]: AccordionsProps[];
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    // prepeare data for the component
    const widgetData = {
        title: ownProps.fields?.title || "",
        accordions:
            ownProps.fields?.accordions?.map(accordion => {
                return {
                    title: accordion.fields?.title,
                };
            }) || [],
    };

    return {
        widgetData,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ContentFeatureStyles {}

export const accordionListStylesStyles: ContentFeatureStyles = {};

const styles = accordionListStylesStyles;

const TabList: FC<Props> = ({ widgetData, id }) => {
    return (
        <StyledWrapper data-test-selector="AccordionList">
            {widgetData?.title && (
                <Typography as="h2" variant="h2">
                    {parse(widgetData?.title, parserOptions)}
                </Typography>
            )}
            <Accordion headingLevel={4}>
                {widgetData.accordions.map((accordion, index) => {
                    return (
                        <AccordionSection key={accordion?.title} title={parse(accordion?.title, parserOptions)}>
                            <Zone contentId={id} zoneName={`content-${index}`} />
                        </AccordionSection>
                    );
                })}
            </Accordion>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(TabList),
    definition: {
        displayName: "Accordion List",
        group: "XNGAGE" as any,
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Widget Title",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
                extendedConfig: { height: 120 },
            },
            {
                name: fields.accordions,
                editorTemplate: "ListField",
                defaultValue: [],
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: accordionFields.title,
                        editorTemplate: "RichTextField",
                        defaultValue: "",
                        isRequired: true,
                        extendedConfig: { height: 120 },
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
